import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Layers, ArrowLeft, Download, Loader2, Dumbbell, ChevronRight } from "lucide-react";
import { toast } from "sonner";

export default function SupersetImport() {
  const { token } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [importing, setImporting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/supersets/by-token/${token}`);
        setData(data);
      } catch (e) {
        setErr(e.response?.data?.detail || "Couldn't load this superset");
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const importIt = async () => {
    setImporting(true);
    try {
      const { data: saved } = await api.post(`/supersets/by-token/${token}/import`);
      toast.success(`Added "${saved.name}" to your library`);
      nav("/library");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not import");
    } finally {
      setImporting(false);
    }
  };

  if (loading) return (
    <div className="max-w-2xl mx-auto p-6 text-neutral-400 flex items-center gap-2">
      <Loader2 className="w-4 h-4 animate-spin" /> Loading…
    </div>
  );
  if (err) return (
    <div className="max-w-2xl mx-auto p-6">
      <div data-testid="ss-import-error" className="border border-neutral-800 p-6 text-center">
        <Layers className="w-8 h-8 text-neutral-600 mx-auto mb-2" />
        <div className="text-sm text-neutral-300">{err}</div>
        <Link to="/library" className="text-xs text-blue-500 uppercase tracking-widest mt-3 inline-block">Back to Library</Link>
      </div>
    </div>
  );

  return (
    <div className="max-w-2xl mx-auto p-4 sm:p-6">
      <button onClick={() => nav(-1)} className="flex items-center text-neutral-400 hover:text-blue-500 text-xs uppercase tracking-widest mb-4">
        <ArrowLeft className="w-4 h-4 mr-1" /> Back
      </button>

      <div className="text-xs tracking-[0.3em] uppercase text-blue-500 font-bold mb-1">Shared Superset</div>
      <h1 data-testid="ss-import-name" className="font-display text-4xl sm:text-5xl uppercase leading-none">{data.name}</h1>
      <div className="flex items-center gap-2 mt-3 text-xs text-neutral-400">
        <div className="w-7 h-7 bg-blue-500 flex items-center justify-center font-display text-white text-sm overflow-hidden">
          {data.owner_picture ? <img src={data.owner_picture} alt="" className="w-full h-full object-cover" /> : (data.owner_name?.[0] || "?").toUpperCase()}
        </div>
        <span>From <span className="text-white font-semibold">{data.owner_name}</span></span>
        {data.owner_gym && <span className="text-blue-500">@ {data.owner_gym}</span>}
      </div>

      {data.description && (
        <div className="border-l-2 border-blue-500 pl-3 mt-4 text-sm text-neutral-300 italic">{data.description}</div>
      )}

      <div className="border border-blue-500/40 bg-blue-500/5 mt-6 p-4">
        <div className="flex items-center gap-2 mb-3">
          <Layers className="w-4 h-4 text-blue-500" />
          <div className="text-[10px] tracking-[0.3em] uppercase text-blue-500 font-bold">Superset</div>
          <div className="text-xs text-neutral-400 ml-auto">{data.exercises.length} exercises · rest {data.rest_seconds}s after each round</div>
        </div>
        <div className="space-y-2">
          {data.exercises.map((ex, i) => (
            <div key={i} data-testid={`ss-import-ex-${i}`} className="border border-neutral-800 bg-neutral-950 p-3 flex items-center gap-3">
              <div className="w-9 h-9 border border-neutral-800 flex items-center justify-center shrink-0">
                <Dumbbell className="w-4 h-4 text-blue-500" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-display text-base uppercase truncate">{String.fromCharCode(65 + i)}. {ex.name}</div>
                <div className="text-[10px] text-neutral-500 uppercase tracking-wider truncate">
                  {(ex.muscle_groups || []).join(", ")} · {(ex.sets || []).length} sets
                </div>
              </div>
              <ChevronRight className="w-4 h-4 text-neutral-700 shrink-0" />
            </div>
          ))}
        </div>
      </div>

      <Button
        data-testid="ss-import-add-btn"
        onClick={importIt}
        disabled={importing}
        className="w-full bg-blue-500 hover:bg-blue-600 rounded-none uppercase tracking-wider font-display h-12 mt-6"
      >
        {importing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Download className="w-4 h-4 mr-2" />}
        {data.is_own ? "It's already yours" : "Add to my Library"}
      </Button>
      {!data.is_own && (
        <div className="text-xs text-neutral-500 text-center mt-3">
          You'll get your own copy — edits to your version won't affect the original.
        </div>
      )}
    </div>
  );
}
